import React from "react";
import { JourneyContextComponent } from "./src/helpers/context";
import { TrackingProvider } from "./src/contexts/TrackingContext";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  captureAttribution,
  installAttributionCapture,
} from "./src/helpers/attribution";
import { installAppLinkInterceptor } from "./src/helpers/appHandoff";
import { loadThirdPartyScriptsForDocument } from "./src/helpers/thirdPartyScripts";
import { pushRouteChange, scheduleGtm } from "./src/helpers/gtm";
import { initPosthog } from "./src/helpers/posthog";
import { sendPartnerLandingHit } from "./src/helpers/partnerLandingHit";
import { MarkdownHtmlContext } from "./src/components/Markdown/MarkdownHtml";

import "./src/styles/global.css";
import "./src/styles/components.css";
import "./src/styles/markdown.css";
// Latin-only, one file per weight. The full `@fontsource/<family>/<weight>.css`
// files declare every subset the family ships (cyrillic, cyrillic-ext,
// vietnamese, latin-ext, latin) — 26 @font-face rules that Gatsby inlines into
// every HTML document for ~355 KB of CSS, to serve markets whose copy is pure
// latin-1 (ES/FR/IT/PT all fit in U+0000-00FF). The latin-only files carry one
// rule each, same woff2 the browser was already picking.
//
// Weights kept, verified against the class inventory in src/:
//   DM Sans 400   — body copy, `font-normal`, the implicit default
//   DM Sans 500   — `font-medium` (30 uses) + the storybook's input styling
//   DM Sans 700   — `font-bold` (214 uses) and every h1-h6 via global.css
//   Merriweather 300 — `font-MW font-light`, the hero H1 of 7 hero variants
//   Merriweather 400 — bare `font-MW` section subtitles, `font-MW font-normal`
//   Merriweather 700 — `font-MW font-bold`
// Dropped: Merriweather 900 — no element combines `font-MW` with `font-black`
// (the three `font-black` uses are DM Sans, which has no 900 face here and was
// already being synthesised by the browser).
// Italic is unchanged: fontsource ships italics in separate `*-italic.css`
// files that were never imported, so the 12 `font-MW ... italic` usages were
// already faux-italic and still are.
import "@fontsource/dm-sans/latin-400.css";
import "@fontsource/dm-sans/latin-500.css";
import "@fontsource/dm-sans/latin-700.css";
import "@fontsource/merriweather/latin-300.css";
import "@fontsource/merriweather/latin-400.css";
import "@fontsource/merriweather/latin-700.css";

// The storybook's Tailwind build. It used to arrive as a side effect of
// `lib/index.js` (the barrel), which every component import pulled in; the
// components are now deep-imported (one module each) and nothing imports the
// barrel any more, so the stylesheet has to be requested explicitly. It is
// still needed: get-life-storybook-ts ships its markup with Tailwind utility
// classes, and this repo's tailwind.config.js `content` does not scan
// node_modules, so those classes exist in no other stylesheet.
// Imported here, after our own CSS and the fontsource faces, to keep the exact
// cascade order the barrel produced (ours first, storybook last, per-component
// storybook CSS after that via the component modules).
// What the barrel also dragged in and this does NOT: the three full
// `@fontsource/dm-sans/{400,500,700}.css` files (6 duplicate @font-face rules
// for faces already declared latin-only above) and the base64 icomoon EOT of
// atoms/ProgressiveButton, a component the site never renders.
//
// Kept after measuring the alternative (bundle-diet brief §5 "option B": put
// node_modules/get-life-storybook-ts/lib/**/*.js in tailwind.config.js `content`
// and drop this import). It does not work, for four reasons found on
// 2026-09-15 by diffing the selectors of this file against a Tailwind build of
// this repo's config with `lib` added to `content` (1.413 selectors would be
// lost):
//   1. The library's Tailwind theme is its own -- `alert`, `error-100`,
//      `info-600`, `gray-text`, `green-truspilot`, `deep-life-blue-*` and the
//      `.H1`/`.H2`/`.BodyL`/`.BodyM`/`.BodyS` type classes (TrustPilotDS.tsx:23
//      picks one of those at runtime). This config knows none of them.
//   2. index.css carries @tailwindcss/forms' base layer (12,7 KB of
//      `[type='text']`, `[type='checkbox']`... resets) and the plugin is not a
//      dependency of this repo; every storybook input in the calculator is
//      styled on top of it.
//   3. Its `:root` block is where --primary-color / --dark-primary-color /
//      --theme-* are *defined*. This repo only ever reads them (tailwind.config
//      `purple`, `deepLifeBlue`, global.css, components.css).
//   4. ~22 KB of global CSS that is not a utility at all: body/a defaults, the
//      `input:not(.inputDS input)` family, ::-webkit-scrollbar, #customDateInput
//      and 13 @keyframes.
// Regenerating the utilities here would also put `!important` on every one of
// them (this config sets `important: true`, the library's does not).
// The saving was real (~78 KB minified) but it has to come from the library:
// a published Tailwind preset + per-component CSS, or a purge step over this
// file. Neither is possible offline in this PR.
// Purged copy of get-life-storybook-ts/lib/index.css — same file, minus the
// 40 % of rules whose classes appear in no source of this site. Generated by
// scripts/purge-storybook-css.js, re-run by gatsby-node.ts onPreBootstrap on
// every build, and committed so the diff is reviewable. Importing the purged
// copy instead of the original keeps the cascade order identical (it is the
// same rules in the same order, just fewer of them).
import "./src/styles/storybook.purged.css";

if (process.env.NODE_ENV === "development") {
  require("./src/styles/dev.css");
}

let posthogInitialized = false;
const deferTask = typeof window !== 'undefined' && window.requestIdleCallback
  ? window.requestIdleCallback
  : (cb) => setTimeout(cb, 1);

// Auto-resize GLOBAL de los formularios del CRM embebidos por iframe
// (crm.life5.com/f/*?embed=1): la página embebida publica su altura real con
// postMessage({ type: 'crm-form:height', height }). El componente Iframe
// (component.iframe) ya escucha por su cuenta; este listener global cubre los
// iframes pegados como HTML crudo (bloques basic.html de blogs — ahí los
// <script> inline no se ejecutan porque el bloque se monta con
// dangerouslySetInnerHTML). Solo se aceptan mensajes del origin del CRM y se
// ajusta únicamente el iframe cuyo contentWindow emitió el mensaje.
const CRM_FORM_ORIGIN = "https://crm.life5.com";
export const onClientEntry = () => {
  // Attribution used to be captured by a useEffect inside <Button/>, so it only
  // ran when a Button mounted and never again after CookieYes wiped the cookies
  // while consent was pending. Capturing here covers every page, and the module
  // re-captures on the CMP consent event.
  installAttributionCapture();

  // Safety net for the hop to the app. `buildAppHandoffUrl` is only called
  // explicitly by the calculator controllers; every other CTA is a plain
  // `<a href="https://app.axa.life5.es/calculator/1">` (Strapi blocks,
  // hardcoded links) whose attribution depended entirely on cookies the CMP
  // may have deleted. This capture-phase listener rewrites the href of any
  // anchor pointing at one of our app hosts right before the navigation.
  installAppLinkInterceptor();

  // Trustpilot / Typeform used to load on every page from wrapRootElement.
  // Now they load at idle only when this page actually renders their markup.
  loadThirdPartyScriptsForDocument();

  window.addEventListener("message", (event) => {
    if (event.origin !== CRM_FORM_ORIGIN) return;
    const message = event.data;
    if (!message || message.type !== "crm-form:height") return;
    const height = message.height;
    if (typeof height !== "number" || !Number.isFinite(height) || height <= 0) return;
    const frames = document.querySelectorAll("iframe");
    for (const frame of frames) {
      if (frame.contentWindow === event.source) {
        frame.style.height = `${Math.ceil(height)}px`;
        break;
      }
    }
  });
};

// Client side navigations do not re-run onClientEntry, and each page's Button
// used to re-capture on mount. Keep capturing so a link with fresh UTMs (or a
// cookie the CMP removed meanwhile) is picked up on every route change.
export const onRouteUpdate = ({ location }) => {
  captureAttribution();
  // Per-click partner landings (life5.it/facile/?externalReference=FIT-…): the
  // CRM counts each click id once for the partner's billing. No-op on any
  // other page or without the click id. See src/helpers/partnerLandingHit.ts.
  sendPartnerLandingHit(location);
  // A client-side navigation can land on a page that does render a widget.
  loadThirdPartyScriptsForDocument();
  // Was gatsby-plugin-google-tagmanager's own onRouteUpdate. GA4 page_view (and
  // every other tag bound to `gatsby-route-change`) depends on this push, so it
  // has to keep firing on the initial render AND on every client-side
  // navigation, whether or not gtm.js has loaded yet — a push before the
  // container exists is queued in dataLayer and replayed on load.
  if (process.env.NODE_ENV === "production") {
    pushRouteChange();
  }
};

// gtm.js used to be a synchronous <script> in <head> injected by
// gatsby-plugin-google-tagmanager, competing with hydration for the main
// thread. It now loads on the first user interaction, or at the first idle
// slot from 2 s after this point, whichever comes first. See
// src/helpers/gtm.ts.
export const onInitialClientRender = () => {
  if (process.env.NODE_ENV === "production") {
    scheduleGtm();
  }
};

// Field diagnostics for the INP workstream (2026-07/08): CrUX only gives the
// p75 number; these events say WHICH interaction caused it and in which phase
// (input delay vs processing vs presentation). INP uses reportAllChanges so
// delivery never depends on pagehide beacons — dedupe in analysis by
// metric_id keeping the max value. CLS is intentionally not tracked (field
// CLS is 0 across the site).
const reportWebVitals = (posthog) => {
  import("web-vitals/attribution")
    .then(({ onINP, onLCP }) => {
      onINP(
        (metric) => {
          const a = metric.attribution || {};
          posthog.capture("web_vitals_inp", {
            value: Math.round(metric.value),
            rating: metric.rating,
            metric_id: metric.id,
            navigation_type: metric.navigationType,
            pathname: window.location.pathname,
            interaction_target: a.interactionTarget,
            interaction_type: a.interactionType,
            input_delay: Math.round(a.inputDelay || 0),
            processing_duration: Math.round(a.processingDuration || 0),
            presentation_delay: Math.round(a.presentationDelay || 0),
            load_state: a.loadState,
          });
        },
        { reportAllChanges: true }
      );
      onLCP((metric) => {
        const a = metric.attribution || {};
        posthog.capture("web_vitals_lcp", {
          value: Math.round(metric.value),
          rating: metric.rating,
          metric_id: metric.id,
          navigation_type: metric.navigationType,
          pathname: window.location.pathname,
          lcp_target: a.target,
          ttfb: Math.round(a.timeToFirstByte || 0),
          resource_load_delay: Math.round(a.resourceLoadDelay || 0),
          resource_load_duration: Math.round(a.resourceLoadDuration || 0),
          element_render_delay: Math.round(a.elementRenderDelay || 0),
        });
      });
    })
    .catch(() => {
      // web-vitals unavailable (very old browser) — no diagnostics, no break.
    });
};

export const wrapRootElement = ({ element }) => {
  if (!posthogInitialized) {
    posthogInitialized = true;
    if (typeof window !== 'undefined') {
      // The dynamic import lives in src/helpers/posthog.ts, which is also the
      // only way the calculator controllers reach the library — they used to
      // `import posthog from "posthog-js"` statically, which hoisted its
      // vendor chunk back into every page's initial chunk group and undid this
      // deferral.
      deferTask(() => {
        initPosthog((posthog) => {
          posthog.capture('$pageview');
          reportWebVitals(posthog);
        });
      });
    }
  }
  return (
    <HelmetProvider>
      <TrackingProvider>
      <JourneyContextComponent>
        <Helmet>
          <meta
            name="google-site-verification"
            content="8ctcpBxOiSj32Kc4N2gPuz5As7GYh-6W7hNrDZhLXb4"
          />
        </Helmet>
        {element}
      </JourneyContextComponent>
      </TrackingProvider>
    </HelmetProvider>
  );
};

// Strapi markdown is rendered to HTML at build time (gatsby-node.ts ->
// src/helpers/markdownToHtml.ts) and travels in the page context. Publishing it
// through a context here is what lets every block component print its field
// with dangerouslySetInnerHTML instead of shipping react-markdown, rehype-raw
// (parse5) and remark-gfm (micromark) to the browser.
export const wrapPageElement = ({ element, props }) => (
  <MarkdownHtmlContext.Provider value={props?.pageContext?.markdownHtml || null}>
    {element}
  </MarkdownHtmlContext.Provider>
);
